
import React from 'react';
import { NotificationType, Notification } from '@/types/notification';
import { formatDistanceToNow } from 'date-fns';
import {
  Bell,
  Calendar,
  MessageCircle,
  ClipboardList,
  FileText,
  Star,
  Image,
  FileEdit,
  Megaphone,
  AlertTriangle,
  BadgeAlert,
  Zap,
  AlertCircle,
  Check,
  Plane,
  CreditCard,
  Tag,
  BarChart,
  Clock,
  Mail
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';

interface NotificationItemProps {
  notification: Notification;
  showActions?: boolean;
  onMarkAsRead?: (id: string) => void;
}

const getNotificationIcon = (type: NotificationType) => {
  switch (type) {
    case 'new_case':
      return <Zap className="h-5 w-5 text-blue-500" />;
    case 'appointment':
      return <Calendar className="h-5 w-5 text-green-500" />;
    case 'message':
      return <MessageCircle className="h-5 w-5 text-violet-500" />;
    case 'new_chat_message':
      return <Mail className="h-5 w-5 text-blue-500" />;
    case 'message_attachment':
      return <FileText className="h-5 w-5 text-violet-500" />;
    case 'follow_up':
      return <ClipboardList className="h-5 w-5 text-amber-500" />;
    case 'post_treatment':
      return <FileText className="h-5 w-5 text-red-500" />;
    case 'review':
      return <Star className="h-5 w-5 text-yellow-500" />;
    case 'content_approval':
      return <Image className="h-5 w-5 text-pink-500" />;
    case 'treatment_plan':
      return <FileEdit className="h-5 w-5 text-cyan-500" />;
    case 'admin_message':
      return <Megaphone className="h-5 w-5 text-indigo-500" />;
    case 'document':
      return <AlertTriangle className="h-5 w-5 text-orange-500" />;
    case 'pending_approval':
      return <BadgeAlert className="h-5 w-5 text-amber-500" />;
    case 'case_status':
      return <Check className="h-5 w-5 text-green-500" />;
    case 'performance':
      return <AlertCircle className="h-5 w-5 text-red-500" />;
    case 'travel':
      return <Plane className="h-5 w-5 text-blue-500" />;
    case 'finance':
      return <CreditCard className="h-5 w-5 text-emerald-500" />;
    case 'promo':
      return <Tag className="h-5 w-5 text-purple-500" />;
    case 'appointment_change':
      return <Calendar className="h-5 w-5 text-blue-500" />;
    case 'travel_update':
      return <Plane className="h-5 w-5 text-blue-500" />;
    case 'ad_expiration':
      return <Clock className="h-5 w-5 text-orange-500" />;
    case 'ad_performance':
      return <BarChart className="h-5 w-5 text-green-500" />;
    case 'low_performing_ads':
      return <BarChart className="h-5 w-5 text-red-500" />;
    default:
      return <Bell className="h-5 w-5 text-gray-500" />;
  }
};

const NotificationItem: React.FC<NotificationItemProps> = ({
  notification,
  showActions = false,
  onMarkAsRead
}) => {
  const isChatNotification = notification.type === 'new_chat_message' || notification.type === 'message_attachment';
  
  return (
    <div className={`p-4 ${!notification.isRead ? 'bg-primary/5' : ''}`}>
      <div className="flex items-start gap-3">
        {isChatNotification && notification.senderAvatar ? (
          <Avatar className="mt-1 h-8 w-8">
            <AvatarImage src={notification.senderAvatar} alt={notification.senderName || 'User'} />
            <AvatarFallback>{notification.senderName?.[0] || 'U'}</AvatarFallback>
          </Avatar>
        ) : (
          <div className="mt-1">{getNotificationIcon(notification.type)}</div>
        )}
        
        <div className="flex-1">
          <h4 className="font-medium text-sm">{notification.title}</h4>
          <p className="text-sm text-muted-foreground mt-1">{notification.message}</p>
          <div className="mt-2 flex items-center justify-between">
            <span className="text-xs text-muted-foreground">
              {formatDistanceToNow(new Date(notification.timestamp), { addSuffix: true })}
            </span>
            
            {notification.actionText && showActions && (
              <Button size="sm" variant="outline" className="text-xs h-6 px-2">
                {notification.actionText}
              </Button>
            )}
            
            {!notification.isRead && showActions && onMarkAsRead && (
              <Button 
                size="sm" 
                variant="ghost" 
                className="text-xs h-6 px-2"
                onClick={(e) => {
                  e.stopPropagation();
                  onMarkAsRead(notification.id);
                }}
              >
                Mark as read
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotificationItem;
